import { SupabaseClient } from '@supabase/supabase-js'
import { ConflictError, NotFoundError, ValidationError } from '@repo/errors'
import { Database } from '../types/database'
import { v4 as uuidv4 } from 'uuid'
import { format } from 'date-fns'
import { zonedDate, formatInTz, parseInZone } from '@repo/time'

export interface AvailabilityData {
  id?: string
  proId: string
  start: string
  end: string
  isFollowup?: boolean
  isBlock?: boolean
  isPD?: boolean
  notes?: string
  meetingId?: string
}

export interface CreateAvailabilityData {
  start: string
  end: string
  isFollowup?: boolean
  isBlock?: boolean
  notes?: string
}

class AvailabilityService {
  constructor(private supabase: SupabaseClient<Database>) {}

  async checkConflicts(proId: string, start: Date, end: Date, excludeId?: string, therapistTimezone?: string, isBlock?: boolean) {
    console.log('🔍 [checkConflicts] Checking conflicts for:', {
      proId,
      start: start.toISOString(),
      end: end.toISOString(),
      excludeId,
      therapistTimezone,
      isBlock
    })
    
    // Check for any overlapping availability slots
    // A conflict exists if any of these conditions are true:
    // 1. New slot starts during an existing slot
    // 2. New slot ends during an existing slot  
    // 3. New slot completely contains an existing slot
    // 4. Existing slot completely contains the new slot
    
    // First, let's get all availability for this pro and check manually
    let query = (this.supabase
      .from('availability') as any)
      .select('*')
      .eq('proId', proId)
    
    // Only add the neq filter if excludeId is provided
    if (excludeId) {
      query = query.neq('id', excludeId)
    }

    const { data: allAvailability, error } = await query

    if (error) {
      console.error('Error checking conflicts:', error)
      throw error
    }

    // Type the availability data properly since database types may be out of sync
    type AvailabilityRow = {
      id: string
      proId: string
      start: string
      end: string
      isBlock?: boolean
      isFollowup?: boolean
      isPD?: boolean
      notes?: string
      meetingId?: string
    }

    // Manually check for overlaps
    const conflicts = ((allAvailability || []) as AvailabilityRow[]).filter(existing => {
      // If we're creating a block, only check conflicts with other blocks
      // Blocks can overlap with regular availability
      if (isBlock && !existing.isBlock) {
        return false
      }
      // Database stores UTC times without 'Z' suffix
      // Add 'Z' to ensure they're treated as UTC
      const existingStartUTCStr = existing.start.endsWith('Z') ? existing.start : existing.start + 'Z'
      const existingEndUTCStr = existing.end.endsWith('Z') ? existing.end : existing.end + 'Z'
      
      // For comparison, parse the UTC strings to get timestamp values
      // This avoids timezone issues by working directly with milliseconds
      const existingStartTime = Date.parse(existingStartUTCStr)
      const existingEndTime = Date.parse(existingEndUTCStr)
      const newStartTime = start.getTime()
      const newEndTime = end.getTime()
      
      console.log('🔎 [checkConflicts] Raw values:', {
        newSlot: {
          startUTC: start.toISOString(),
          endUTC: end.toISOString(),
          startTime: newStartTime
        },
        existingSlot: {
          id: existing.id,
          startRaw: existing.start,
          endRaw: existing.end,
          startWithZ: existingStartUTCStr,
          endWithZ: existingEndUTCStr,
          startTime: existingStartTime,
          endTime: existingEndTime
        }
      })
      
      // Log times in therapist timezone for debugging
      if (therapistTimezone) {
        // Use formatInTz directly with the UTC strings
        const newStartLocal = formatInTz(start, therapistTimezone, 'yyyy-MM-dd h:mm a')
        const newEndLocal = formatInTz(end, therapistTimezone, 'yyyy-MM-dd h:mm a')
        const existingStartLocal = formatInTz(existingStartUTCStr, therapistTimezone, 'yyyy-MM-dd h:mm a')
        const existingEndLocal = formatInTz(existingEndUTCStr, therapistTimezone, 'yyyy-MM-dd h:mm a')
        
        console.log('🕐 [checkConflicts] Times in therapist timezone:', {
          timezone: therapistTimezone,
          newSlot: {
            start: newStartLocal,
            end: newEndLocal
          },
          existingSlot: {
            id: existing.id,
            start: existingStartLocal,
            end: existingEndLocal,
            isBlock: existing.isBlock
          }
        })
      }
      
      // Check for any overlap using timestamps
      // Adjacent slots are allowed (e.g., 12-2pm and 2-3pm)
      const hasOverlap = (
        (newStartTime < existingEndTime && newEndTime > existingStartTime)
      )
      
      console.log('🔍 Overlap check result:', {
        hasOverlap,
        condition1: `newStart < existingEnd: ${newStartTime} < ${existingEndTime} = ${newStartTime < existingEndTime}`,
        condition2: `newEnd > existingStart: ${newEndTime} > ${existingStartTime} = ${newEndTime > existingStartTime}`
      })
      
      if (hasOverlap) {
        console.log('❌ Overlap found')
      }
      
      return hasOverlap
    })

    console.log('Found conflicts:', conflicts)
    return conflicts
  }

  async create(data: CreateAvailabilityData, profileId: string, timezone: string) {
    console.log('Creating availability with data:', data)
    
    if (!profileId) {
      throw new Error('Profile ID is required to create availability')
    }
    
    if (!timezone) {
      throw new Error('Timezone is required to create availability')
    }
    
    // Use provided profile ID and timezone
    const pro = { 
      id: profileId, 
      timezone: timezone 
    }

    console.log('Using pro:', pro)
    console.log('Therapist timezone:', pro.timezone)

    // Generate UUID manually since the database default might not be working
    const id = uuidv4()
    
    const now = new Date().toISOString()
    
    // Convert therapist's local time to UTC for storage
    const therapistTimezone = pro.timezone || 'UTC'
    console.log('Therapist timezone:', therapistTimezone)
    
    // Convert the incoming dates from therapist's local time to UTC
    // IMPORTANT: We must avoid using the machine's timezone
    
    // Ensure we have seconds
    const startStr = data.start.match(/:\d{2}:\d{2}$/) ? data.start : data.start + ':00'
    const endStr = data.end.match(/:\d{2}:\d{2}$/) ? data.end : data.end + ':00'
    
    // The input strings represent times in the therapist's timezone
    // We need to parse them without any timezone interpretation
    // Then tell date-fns-tz that these times are in the therapist's timezone
    
    // Extract date and time components manually to avoid timezone issues
    const parseDateTime = (dateTimeStr: string) => {
      const match = dateTimeStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/)
      if (!match) throw new ValidationError(`Invalid datetime format: ${dateTimeStr}`)
      return {
        year: parseInt(match[1]!),
        month: parseInt(match[2]!), 
        day: parseInt(match[3]!),
        hour: parseInt(match[4]!),
        minute: parseInt(match[5]!),
        second: parseInt(match[6]!)
      }
    }
    
    const startParts = parseDateTime(startStr)
    const endParts = parseDateTime(endStr)
    
    // Create a date string that represents the exact time components
    // without any timezone marker, then parse it as the therapist's timezone
    const startDateStr = `${startParts.year}-${String(startParts.month).padStart(2, '0')}-${String(startParts.day).padStart(2, '0')} ${String(startParts.hour).padStart(2, '0')}:${String(startParts.minute).padStart(2, '0')}:${String(startParts.second).padStart(2, '0')}`
    const endDateStr = `${endParts.year}-${String(endParts.month).padStart(2, '0')}-${String(endParts.day).padStart(2, '0')} ${String(endParts.hour).padStart(2, '0')}:${String(endParts.minute).padStart(2, '0')}:${String(endParts.second).padStart(2, '0')}`
    
    // Use zonedTimeToUtc (which is an alias for parseInZone) to convert
    // The key is that we're telling date-fns-tz: "this string represents a time in therapistTimezone"
    // It will then calculate what UTC time that corresponds to
    const startUTC = parseInZone(new Date(startDateStr), therapistTimezone)
    const endUTC = parseInZone(new Date(endDateStr), therapistTimezone)
    
    // Check for conflicts
    const conflictCheckData = {
      proId: pro.id,
      startUTC: startUTC.toISOString(),
      endUTC: endUTC.toISOString(),
      therapistTimezone,
      isBlock: data.isBlock
    }
    
    const conflicts = await this.checkConflicts(pro.id, startUTC, endUTC, undefined, therapistTimezone, data.isBlock)
    
    if (conflicts.length > 0) {
      // Convert conflict times to therapist's timezone for display
      const conflictDetails = conflicts.map(c => {
        // Database stores UTC without 'Z', so we need to add it
        const conflictStartUTCStr = c.start.endsWith('Z') ? c.start : c.start + 'Z'
        const conflictEndUTCStr = c.end.endsWith('Z') ? c.end : c.end + 'Z'
        
        // Format in therapist timezone for display
        const conflictStartLocal = formatInTz(conflictStartUTCStr, therapistTimezone, 'h:mm a')
        const conflictEndLocal = formatInTz(conflictEndUTCStr, therapistTimezone, 'h:mm a')
        
        return {
          start: conflictStartLocal,
          end: conflictEndLocal,
          type: c.isBlock ? 'blocked' : c.isFollowup ? 'follow-up' : 'available',
          notes: c.notes
        }
      })
      console.error('Availability conflicts found:', conflictDetails)
      const conflict = conflictDetails[0]
      // Blocked-slot notes are therapist free-text — safe when the current
      // viewer IS that therapist (the only path today), but if this ever
      // gets exposed cross-user the notes should not appear in the wire
      // message. Show only the slot type + time range.
      const message = conflict
        ? `Time slot conflicts with existing ${conflict.type} slot from ${conflict.start} to ${conflict.end}`
        : 'Time slot conflicts with existing availability'
      throw new ConflictError(message)
    }
    
    const insertData = {
      id,
      proId: pro.id,
      start: startUTC.toISOString(),
      end: endUTC.toISOString(),
      isFollowup: data.isFollowup || false,
      isBlock: data.isBlock || false,
      notes: data.notes || null,
      createdAt: now,
      updatedAt: now
    }
    
    console.log('Time conversion:', {
      therapistTimezone,
      localStart: data.start,
      utcStart: startUTC.toISOString(),
      localEnd: data.end,
      utcEnd: endUTC.toISOString()
    })
    console.log('Inserting availability data:', insertData)
    
    const { data: availability, error } = await (this.supabase
      .from('availability') as any)
      .insert(insertData)
      .select()
      .single()

    if (error) {
      console.error('❌ [AvailabilityService] Insert failed:', {
        error: error,
        errorMessage: error.message,
        errorCode: error.code,
        errorDetails: error.details,
        fullError: JSON.stringify(error, null, 2),
        attemptedData: insertData,
        proId: pro.id,
        timezone: pro.timezone
      })
      throw error
    }
    
    console.log('Successfully created availability:', availability)
    return availability
  }

  async createMany(slots: CreateAvailabilityData[], profileId: string, timezone: string) {
    if (!profileId) {
      throw new Error('Profile ID is required to create availability')
    }
    
    if (!timezone) {
      throw new Error('Timezone is required to create availability')
    }
    
    // Use provided profile ID and timezone
    const pro = { 
      id: profileId, 
      timezone: timezone 
    }

    const now = new Date().toISOString()
    const therapistTimezone = pro.timezone
    console.log('Therapist timezone:', therapistTimezone)
    
    // Helper function to parse datetime consistently
    const parseDateTime = (dateTimeStr: string) => {
      const match = dateTimeStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/)
      if (!match) throw new ValidationError(`Invalid datetime format: ${dateTimeStr}`)
      return {
        year: parseInt(match[1]!),
        month: parseInt(match[2]!), 
        day: parseInt(match[3]!),
        hour: parseInt(match[4]!),
        minute: parseInt(match[5]!),
        second: parseInt(match[6]!)
      }
    }
    
    // Check for conflicts for all slots
    const conflictingSlots = []
    for (const slot of slots) {
      // Ensure we have seconds
      const startStr = slot.start.match(/:\d{2}:\d{2}$/) ? slot.start : slot.start + ':00'
      const endStr = slot.end.match(/:\d{2}:\d{2}$/) ? slot.end : slot.end + ':00'
      
      const startParts = parseDateTime(startStr)
      const endParts = parseDateTime(endStr)
      
      // Create date strings without timezone markers
      const startDateStr = `${startParts.year}-${String(startParts.month).padStart(2, '0')}-${String(startParts.day).padStart(2, '0')} ${String(startParts.hour).padStart(2, '0')}:${String(startParts.minute).padStart(2, '0')}:${String(startParts.second).padStart(2, '0')}`
      const endDateStr = `${endParts.year}-${String(endParts.month).padStart(2, '0')}-${String(endParts.day).padStart(2, '0')} ${String(endParts.hour).padStart(2, '0')}:${String(endParts.minute).padStart(2, '0')}:${String(endParts.second).padStart(2, '0')}`
      
      // Convert from therapist timezone to UTC
      const startUTC = parseInZone(new Date(startDateStr), therapistTimezone)
      const endUTC = parseInZone(new Date(endDateStr), therapistTimezone)
      
      const conflicts = await this.checkConflicts(pro.id, startUTC, endUTC, undefined, therapistTimezone, slot.isBlock)
      if (conflicts.length > 0) {
        conflictingSlots.push({
          slot,
          conflicts: conflicts.map(c => {
            // Convert conflict times to therapist's timezone for display
            const conflictStartUTC = new Date(c.start + (c.start.endsWith('Z') ? '' : 'Z'))
            const conflictEndUTC = new Date(c.end + (c.end.endsWith('Z') ? '' : 'Z'))
            const conflictStartLocal = zonedDate(conflictStartUTC, therapistTimezone)
            const conflictEndLocal = zonedDate(conflictEndUTC, therapistTimezone)
            
            return {
              start: format(conflictStartLocal, 'h:mm a'),
              end: format(conflictEndLocal, 'h:mm a'),
              type: c.isBlock ? 'blocked' : c.isFollowup ? 'follow-up' : 'available',
              notes: c.notes
            }
          })
        })
      }
    }
    
    if (conflictingSlots.length > 0) {
      const firstConflict = conflictingSlots[0]
      console.error(`${conflictingSlots.length} slots have conflicts`, conflictingSlots)
      const conflict = firstConflict && firstConflict.conflicts && firstConflict.conflicts[0]
      // See episode.ts / availability.ts:257 rationale: never inline
      // therapist-authored notes into a wire-visible error string.
      const message = conflict
        ? `${conflictingSlots.length} time slots conflict with existing availability. First conflict: ${conflict.type} slot from ${conflict.start} to ${conflict.end}`
        : `${conflictingSlots.length} time slots conflict with existing availability`
      throw new ConflictError(message)
    }
    
    const slotsWithProId = slots.map(slot => {
      // Ensure we have seconds
      const startStr = slot.start.match(/:\d{2}:\d{2}$/) ? slot.start : slot.start + ':00'
      const endStr = slot.end.match(/:\d{2}:\d{2}$/) ? slot.end : slot.end + ':00'
      
      const startParts = parseDateTime(startStr)
      const endParts = parseDateTime(endStr)
      
      // Create date strings without timezone markers
      const startDateStr = `${startParts.year}-${String(startParts.month).padStart(2, '0')}-${String(startParts.day).padStart(2, '0')} ${String(startParts.hour).padStart(2, '0')}:${String(startParts.minute).padStart(2, '0')}:${String(startParts.second).padStart(2, '0')}`
      const endDateStr = `${endParts.year}-${String(endParts.month).padStart(2, '0')}-${String(endParts.day).padStart(2, '0')} ${String(endParts.hour).padStart(2, '0')}:${String(endParts.minute).padStart(2, '0')}:${String(endParts.second).padStart(2, '0')}`
      
      // Convert from therapist timezone to UTC
      const startUTC = parseInZone(new Date(startDateStr), therapistTimezone)
      const endUTC = parseInZone(new Date(endDateStr), therapistTimezone)
      
      return {
        id: uuidv4(),
        proId: pro.id,
        start: startUTC.toISOString(),
        end: endUTC.toISOString(),
        isFollowup: slot.isFollowup || false,
        isBlock: slot.isBlock || false,
        notes: slot.notes || null,
        createdAt: now,
        updatedAt: now
      }
    })

    const { data: availability, error } = await (this.supabase
      .from('availability') as any)
      .insert(slotsWithProId)
      .select()

    if (error) {
      console.error('Availability createMany error:', error)
      console.error('Attempted slots count:', slotsWithProId.length)
      throw error
    }
    return availability
  }

  async getByDateRange(startDate: string, endDate: string, profileId: string, timezone: string) {
    if (!profileId) {
      throw new Error('Profile ID is required to get availability')
    }
    
    if (!timezone) {
      throw new Error('Timezone is required to get availability')
    }
    
    // Use provided profile ID and timezone
    const pro = { 
      id: profileId, 
      timezone: timezone 
    }
    
    const therapistTimezone = pro.timezone || 'UTC'
    

    // First, let's check if there's ANY availability for this pro
    const { data: allAvailability, error: allError } = await (this.supabase
      .from('availability') as any)
      .select('id, start, end, isBlock')
      .eq('proId', pro.id)
      .order('start', { ascending: false })
      .limit(5)
    

    const { data: availability, error } = await (this.supabase
      .from('availability') as any)
      .select(
        // Embed the linked event (Postgrest join via availability_eventId_fkey)
        // so /schedule can render event-specific cards without a second round-trip.
        '*, event:events(id, title, description, type, "audienceScope", "enrollmentMode", "meetingLink", "pdEligible", "hostProId", host:pros!events_hostProId_fkey("firstName", "lastName"))',
      )
      .eq('proId', pro.id)
      .gte('start', startDate)
      .lte('end', endDate)
      .order('start', { ascending: true })


    if (error) throw error

    // Return availability with therapist timezone info for proper display
    const availabilityWithTimezone = (availability || []).map((avail: any) => {
      // Database returns UTC timestamps without 'Z' suffix
      // We'll pass them as strings and let the frontend handle timezone conversion
      return {
        ...avail,
        // Keep timestamps as strings from database (they represent UTC)
        start: avail.start,
        end: avail.end,
        // Include therapist timezone for display formatting
        therapistTimezone,
        // Ensure meetingId is in camelCase
        meetingId: avail.meetingId || avail.meeting_id,
        // Flatten the embedded event so consumers get a clean `event` object
        // with the host's name already resolved.
        event: avail.event
          ? {
              id: avail.event.id,
              title: avail.event.title,
              description: avail.event.description,
              type: avail.event.type,
              audienceScope: avail.event.audienceScope,
              enrollmentMode: avail.event.enrollmentMode,
              meetingLink: avail.event.meetingLink,
              pdEligible: !!avail.event.pdEligible,
              hostName: avail.event.host
                ? [avail.event.host.firstName, avail.event.host.lastName]
                    .filter(Boolean)
                    .join(' ') || null
                : null,
            }
          : null,
        // Flag to indicate these are UTC timestamps without 'Z'
        isUTC: true
      }
    })

    return availabilityWithTimezone
  }

  async update(id: string, data: Partial<CreateAvailabilityData>, profileId: string, timezone: string) {
    if (!profileId) {
      throw new Error('Profile ID is required to update availability')
    }
    
    if (!timezone) {
      throw new Error('Timezone is required to update availability')
    }
    
    // Use provided profile ID and timezone
    const pro = { 
      id: profileId, 
      timezone: timezone 
    }

    const therapistTimezone = pro.timezone
    let updateData: any = { ...data }
    
    // If updating times, convert and check for conflicts
    if (data.start || data.end) {
      // Get current availability to merge with updates
      const { data: current, error: currentError } = await (this.supabase
        .from('availability') as any)
        .select('start, end, isBlock')
        .eq('id', id)
        .single()
      
      if (currentError || !current) {
        throw new NotFoundError('Availability')
      }
      
      // Helper function to parse datetime consistently
      const parseDateTime = (dateTimeStr: string) => {
        const match = dateTimeStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/)
        if (!match) throw new ValidationError(`Invalid datetime format: ${dateTimeStr}`)
        return {
          year: parseInt(match[1]!),
          month: parseInt(match[2]!), 
          day: parseInt(match[3]!),
          hour: parseInt(match[4]!),
          minute: parseInt(match[5]!),
          second: parseInt(match[6]!)
        }
      }
      
      // Parse and convert times
      let startUTC: Date
      let endUTC: Date
      
      if (data.start) {
        const startStr = data.start.match(/:\d{2}:\d{2}$/) ? data.start : data.start + ':00'
        const startParts = parseDateTime(startStr)
        const startDateStr = `${startParts.year}-${String(startParts.month).padStart(2, '0')}-${String(startParts.day).padStart(2, '0')} ${String(startParts.hour).padStart(2, '0')}:${String(startParts.minute).padStart(2, '0')}:${String(startParts.second).padStart(2, '0')}`
        startUTC = parseInZone(new Date(startDateStr), therapistTimezone)
      } else {
        // Current times are already in UTC from the database
        startUTC = new Date(current.start + (current.start.endsWith('Z') ? '' : 'Z'))
      }
      
      if (data.end) {
        const endStr = data.end.match(/:\d{2}:\d{2}$/) ? data.end : data.end + ':00'
        const endParts = parseDateTime(endStr)
        const endDateStr = `${endParts.year}-${String(endParts.month).padStart(2, '0')}-${String(endParts.day).padStart(2, '0')} ${String(endParts.hour).padStart(2, '0')}:${String(endParts.minute).padStart(2, '0')}:${String(endParts.second).padStart(2, '0')}`
        endUTC = parseInZone(new Date(endDateStr), therapistTimezone)
      } else {
        // Current times are already in UTC from the database
        endUTC = new Date(current.end + (current.end.endsWith('Z') ? '' : 'Z'))
      }
      
      // Check for conflicts (excluding current availability)
      // Use the current isBlock value unless it's being updated
      const isBlock = data.isBlock !== undefined ? data.isBlock : current.isBlock
      const conflicts = await this.checkConflicts(pro.id, startUTC, endUTC, id, therapistTimezone, isBlock)
      if (conflicts.length > 0) {
        const conflictDetails = conflicts.map(c => ({
          start: format(new Date(c.start + (c.start.endsWith('Z') ? '' : 'Z')), 'h:mm a'),
          end: format(new Date(c.end + (c.end.endsWith('Z') ? '' : 'Z')), 'h:mm a'),
          type: c.isBlock ? 'blocked' : c.isFollowup ? 'follow-up' : 'available'
        }))
        const firstConflict = conflictDetails[0]
        throw new ConflictError(firstConflict
          ? `Time slot conflicts with existing ${firstConflict.type} slot from ${firstConflict.start} to ${firstConflict.end}`
          : 'Time slot conflicts with existing availability')
      }
      
      updateData.start = startUTC.toISOString()
      updateData.end = endUTC.toISOString()
    }
    
    updateData.updatedAt = new Date().toISOString()

    const { data: availability, error } = await (this.supabase
      .from('availability') as any)
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return availability
  }

  async delete(id: string) {
    console.log('🗑️ [AvailabilityService] Deleting availability:', { id })
    
    const { error } = await (this.supabase
      .from('availability') as any)
      .delete()
      .eq('id', id)

    if (error) {
      console.error('❌ [AvailabilityService] Delete failed:', {
        id,
        error,
        errorMessage: error.message,
        errorCode: error.code
      })
      throw error
    }
    
    console.log('✅ [AvailabilityService] Successfully deleted availability:', { id })
    return { success: true }
  }

  async deleteMany(ids: string[]) {
    const { error } = await (this.supabase
      .from('availability') as any)
      .delete()
      .in('id', ids)

    if (error) throw error
    return { success: true }
  }
}

export const createAvailabilityService = (supabase: SupabaseClient<Database>) =>
  new AvailabilityService(supabase)